import { useEffect } from 'react';

/**
 * BodyScrollGuard — Iter389 (filet de sécurité global)
 * =====================================================
 * Composant invisible monté UNE SEULE FOIS dans App.js, indépendamment
 * du changement de route. Garantit qu'aucun lock de scroll orphelin
 * (body.position=fixed, body.overflow=hidden, html.overflow=hidden)
 * ne persiste après :
 *
 *   • un retour navigateur (popstate)
 *   • un retour depuis le cache navigateur (pageshow persisted=true)
 *   • un changement de visibilité (revenir de l'onglet)
 *   • une fin de navigation programmatique
 *
 * RÈGLE D'OR : on ne désactive jamais un lock pendant qu'une modale
 * est OUVERTE. On considère qu'une modale est ouverte si un élément
 * avec [data-modal-open="true"], [role="dialog"][aria-modal="true"],
 * ou la classe `.modal-open` est présent. Dans le doute → on ne touche
 * à rien (priorité à la robustesse des modales).
 *
 * Aucune régression : ce composant n'ajoute aucun preventDefault sur
 * wheel/touch. Il se contente de NETTOYER des styles inline orphelins
 * (== styles que le navigateur conserverait au pageshow back/forward
 * cache même si React a démonté la modale entre-temps).
 */
export default function BodyScrollGuard() {
  useEffect(() => {
    const isModalActuallyOpen = () => {
      // Vérifie qu'au moins un élément modal "vivant" existe dans le DOM.
      // Si aucun → un éventuel body.position=fixed est orphelin.
      const selectors = [
        '[data-modal-open="true"]',
        '[role="dialog"][aria-modal="true"]',
        '.modal-open',
        // Hooks/composants existants
        '[data-state="open"][role="dialog"]',
      ];
      for (const sel of selectors) {
        if (document.querySelector(sel)) return true;
      }
      return false;
    };

    const cleanupOrphanLock = (reason) => {
      const body = document.body;
      const html = document.documentElement;
      const hasLockSig = (
        body.style.position === 'fixed'
        || body.style.overflow === 'hidden'
        || html.style.overflow === 'hidden'
      );
      if (!hasLockSig) return;
      if (isModalActuallyOpen()) return; // modale légitime → on respecte

      // Cleanup orphan
      if (body.style.position === 'fixed') body.style.position = '';
      if (body.style.top) body.style.top = '';
      if (body.style.left) body.style.left = '';
      if (body.style.right) body.style.right = '';
      if (body.style.width) body.style.width = '';
      if (body.style.overflow === 'hidden') body.style.overflow = '';
      if (body.style.height === '100%' || body.style.height === '100vh') body.style.height = '';
      if (html.style.overflow === 'hidden') html.style.overflow = '';
      if (html.style.height === '100%' || html.style.height === '100vh') html.style.height = '';
      // eslint-disable-next-line no-console
      console.debug(`[BodyScrollGuard] Cleaned orphan body lock (reason=${reason})`);
    };

    const onPageShow = (e) => {
      // pageshow.persisted=true = restauration depuis bfcache (back/forward).
      // Cas connu où React ne re-mount pas mais les styles inline persistent.
      cleanupOrphanLock(e?.persisted ? 'bfcache' : 'pageshow');
    };
    const onPopState = () => cleanupOrphanLock('popstate');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') cleanupOrphanLock('visibility');
    };

    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('popstate', onPopState);
    document.addEventListener('visibilitychange', onVisibility);

    // Cleanup initial au mount (au cas où l'app monte avec un body locked,
    // ex: HMR en dev qui re-mounte sans reset, ou SSR hydration race).
    cleanupOrphanLock('initial-mount');

    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return null;
}
